import { getJson } from '@/lib/api';
import type { StatsInterval } from '@/lib/leads';

/**
 * The Analytics → PPC page: a typed fetcher over /api/v3/ads/ppc-analytics.
 * Shapes mirror apps/api's PpcAnalytics (api/v3/ads/ppc-analytics.ts), where
 * every metric is defined.
 */

export const PPC_COMPARE = { none: 'none', previous: 'previous', lastYear: 'last_year' } as const;
export type PpcCompare = (typeof PPC_COMPARE)[keyof typeof PPC_COMPARE];
export const PPC_COMPARES = Object.values(PPC_COMPARE);

export const PPC_SPLIT = { none: 'none', campaign: 'campaign', group: 'group' } as const;
export type PpcSplit = (typeof PPC_SPLIT)[keyof typeof PPC_SPLIT];
export const PPC_SPLITS = Object.values(PPC_SPLIT);

export const PPC_ENTITY_KIND = {
  campaign: 'campaign',
  adGroup: 'ad_group',
  assetGroup: 'asset_group',
  campaignNoGroup: 'campaign_no_group',
  unmatched: 'unmatched',
  other: 'other',
} as const;
export type PpcEntityKind = (typeof PPC_ENTITY_KIND)[keyof typeof PPC_ENTITY_KIND];

/** The folded tail of a split; the chart draws this key in its neutral color. */
export const PPC_OTHER_KEY = 'Other';

export const PPC_METRIC = {
  spend: 'spend',
  impressions: 'impressions',
  clicks: 'clicks',
  ctr: 'ctr',
  cpc: 'cpc',
  conversions: 'conversions',
  costPerConversion: 'costPerConversion',
  conversionRate: 'conversionRate',
  leads: 'leads',
  costPerLead: 'costPerLead',
  proposals: 'proposals',
  costPerProposal: 'costPerProposal',
  booked: 'booked',
  roas: 'roas',
  searchImpressionShare: 'searchImpressionShare',
} as const;
export type PpcMetricKey = (typeof PPC_METRIC)[keyof typeof PPC_METRIC];
/** Display order, everywhere the page lists metrics. */
export const PPC_METRICS = Object.values(PPC_METRIC);

export type PpcMetrics = Readonly<Record<PpcMetricKey, number | null>> & {
  readonly spend: number;
  readonly impressions: number;
  readonly clicks: number;
  readonly conversions: number;
  readonly leads: number;
  readonly proposals: number;
  readonly booked: number;
};

export interface PpcEntity {
  readonly key: string;
  readonly label: string;
  readonly kind: PpcEntityKind;
  readonly campaignName: string;
}

export interface PpcSplitSeries extends PpcEntity {
  readonly series: readonly PpcMetrics[];
}

export interface PpcTableRow extends PpcEntity {
  readonly totals: PpcMetrics;
}

export interface PpcCampaignOption {
  readonly id: string;
  readonly name: string;
  readonly channel: string;
  readonly spend: number;
}

export interface PpcGroupOption {
  readonly key: string;
  readonly name: string;
  readonly kind: typeof PPC_ENTITY_KIND.adGroup | typeof PPC_ENTITY_KIND.assetGroup;
  readonly campaignId: string;
  readonly campaignName: string;
  readonly spend: number;
}

export interface PpcAnalytics {
  readonly range: { readonly from: string; readonly to: string; readonly interval: StatsInterval };
  readonly compare: { readonly from: string; readonly to: string; readonly mode: PpcCompare } | null;
  readonly split: PpcSplit;
  readonly latestDate: string | null;
  readonly periods: readonly string[];
  readonly totals: PpcMetrics;
  readonly compareTotals: PpcMetrics | null;
  readonly series: readonly PpcMetrics[];
  readonly compareSeries: readonly (PpcMetrics | null)[] | null;
  readonly comparePeriods: readonly string[] | null;
  readonly splitSeries: readonly PpcSplitSeries[] | null;
  readonly rows: readonly PpcTableRow[];
  readonly options: { readonly campaigns: readonly PpcCampaignOption[]; readonly groups: readonly PpcGroupOption[] };
  readonly changes: readonly { readonly date: string; readonly title: string }[];
  readonly coverage: { readonly leads: number; readonly matchedLeads: number; readonly groupDataFrom: string | null };
}

export interface PpcAnalyticsQuery {
  readonly from: string;
  readonly to: string;
  readonly interval: StatsInterval;
  readonly compare: PpcCompare;
  readonly split: PpcSplit;
  readonly campaignIds: readonly string[];
  readonly groupKeys: readonly string[];
}

export function readPpcAnalytics(query: PpcAnalyticsQuery, signal?: AbortSignal): Promise<PpcAnalytics> {
  return getJson<PpcAnalytics>(
    '/ads/ppc-analytics',
    {
      from: query.from,
      to: query.to,
      interval: query.interval,
      compare: query.compare,
      split: query.split,
      ...(query.campaignIds.length > 0 ? { campaignIds: query.campaignIds.join(',') } : {}),
      ...(query.groupKeys.length > 0 ? { groupKeys: query.groupKeys.join(',') } : {}),
    },
    signal,
  );
}
